"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMutation = exports.useQuery = void 0;
exports.usePollingQuery = usePollingQuery;
const client_1 = require("@apollo/client");
Object.defineProperty(exports, "useQuery", { enumerable: true, get: function () { return client_1.useQuery; } });
Object.defineProperty(exports, "useMutation", { enumerable: true, get: function () { return client_1.useMutation; } });
const app_utils_1 = require("@sixfold/app-utils");
const typed_primitives_1 = require("@sixfold/typed-primitives");
const React = __importStar(require("react"));
const DEFAULT_FETCH_POLICY = 'cache-first';
const correctLoadingFlag = (loading, variables, state) => {
    if (!loading) {
        return false;
    }
    // We have multiple situations in which we do not want to report ourselves as loading
    // ...during refetch (the easiest polling scenario)
    if (state.networkStatus === client_1.NetworkStatus.refetch) {
        return false;
    }
    // ...during fetchMore (pagination)
    if (state.networkStatus === client_1.NetworkStatus.fetchMore) {
        return false;
    }
    // ...during setVariables when the variables are not actually different from what we have in state
    if (state.networkStatus === client_1.NetworkStatus.setVariables && (0, typed_primitives_1.deepEqual)(variables, state.variables)) {
        return false;
    }
    // ...during loading state when the request is asked to be skipped and variables are the same
    if (state.networkStatus === client_1.NetworkStatus.loading && state.skip && (0, typed_primitives_1.deepEqual)(variables, state.variables)) {
        return false;
    }
    // ...during loading state when we have data, and the variables are the same
    if (state.networkStatus === client_1.NetworkStatus.loading &&
        state.data !== undefined &&
        (0, typed_primitives_1.deepEqual)(variables, state.variables)) {
        return false;
    }
    return true;
};
const shouldUpdateState = (result, state) => {
    // When we transition into ready state
    if (result.networkStatus === client_1.NetworkStatus.ready && state.networkStatus !== client_1.NetworkStatus.ready) {
        return true;
    }
    // When we are in ready state and data changes (unless it goes to undefined and we are skipping)
    if (result.networkStatus === client_1.NetworkStatus.ready && !(0, typed_primitives_1.deepEqual)(result.data, state.data)) {
        if (result.data === undefined && state.skip) {
            return false;
        }
        return true;
    }
    // By default we do not update state
    return false;
};
function usePollingQuery(query, options) {
    const client = (0, client_1.useApolloClient)();
    const defaultFetchPolicy = client.defaultOptions.watchQuery?.fetchPolicy ?? DEFAULT_FETCH_POLICY;
    const { pollInterval, idleUserPollIntervalMultiplier = 10, calculateVariables, variables: propVariables, skip, ...rest } = options;
    const deriveVariables = (data) => calculateVariables?.(data) ?? propVariables;
    const state = React.useRef({
        data: undefined,
        variables: deriveVariables(),
        networkStatus: client_1.NetworkStatus.error,
        skip: false,
    });
    const variables = deriveVariables(state.current.data);
    const onError = (0, app_utils_1.useErrorHandler)();
    const result = (0, client_1.useQuery)(query, {
        ...rest,
        // We want to avoid using cache-and-network, even if that is configured as the default on the client
        fetchPolicy: rest.fetchPolicy ?? (defaultFetchPolicy === 'cache-and-network' ? DEFAULT_FETCH_POLICY : defaultFetchPolicy),
        notifyOnNetworkStatusChange: true,
        skip,
        variables,
        returnPartialData: false,
    });
    (0, app_utils_1.useActiveUserInterval)(async () => {
        try {
            if (result.networkStatus !== client_1.NetworkStatus.ready) {
                // Skip poll if some other process is ongoing (pagination, some long running poll etc.)
                return;
            }
            await result.refetch(deriveVariables(state.current.data));
        }
        catch (e) {
            if (e instanceof Error) {
                onError?.captureError(e);
            }
            else {
                onError?.captureError(new typed_primitives_1.NestedError('Unknown error', e));
            }
        }
    }, {
        activeInterval: pollInterval,
        idleInterval: idleUserPollIntervalMultiplier * pollInterval,
    });
    if (skip === true) {
        state.current.skip = true;
    }
    if (shouldUpdateState(result, state.current)) {
        state.current.data = result.data;
        state.current.variables = deriveVariables(state.current.data);
    }
    if (state.current.networkStatus === client_1.NetworkStatus.ready) {
        state.current.skip = false;
    }
    state.current.networkStatus = result.networkStatus;
    const loading = correctLoadingFlag(result.loading, variables, state.current);
    return {
        ...result,
        loading,
        data: state.current.data,
    };
}
//# sourceMappingURL=hooks.js.map